<template>
    <h2 class="display-sans-serif">{{ getLabel() }}</h2>
    <div class="mt-6 mb-20 max-w-4xl">
        <AnalyticsFilters :show-k-anonymization-error="showKAnonError" @updated="filters = $event" @is-fetching-updated="isFetchingFilters = $event" />
        <h2 class="title mb-4 mx-2 mt-8 md:mt-16">{{ getLabel("nadias_impact") }}</h2>
        <div class="bg-white rounded-lgr border border-[#E5E7EB] p-6 shadow-sm flex flex-col gap-6">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
                <ImpactCard
                    icon="bi bi-stars"
                    :label="getLabel('insights_created').value"
                    :value="insightsData?.total || ''"
                    :is-selected="selectedCard === 'insights'"
                    :is-loading="isFetchingInsights"
                    @click="selectedCard = 'insights'" />
                <ImpactCard
                    icon="bi bi-people-fill"
                    :label="getLabel('relationships_managed').value"
                    :value="relationshipsData?.total || ''"
                    :is-selected="selectedCard === 'relationships'"
                    :is-loading="isFetchingRelationships"
                    @click="selectedCard = 'relationships'" />
                <ImpactCard
                    icon="bi bi-list-ul"
                    :label="getLabel('plans_initiated').value"
                    :value="actionPlansData?.total || ''"
                    :is-selected="selectedCard === 'actionPlans'"
                    :is-loading="isFetchingActionPlans"
                    @click="selectedCard = 'actionPlans'" />
            </div>
            <div v-if="texts.helpText" class="border border-lgr border-[#545466] italic text-[#4A5565] body-small rounded-lgr py-3 px-4">
                {{ texts.helpText }}
            </div>
            <div>
                <div class="title-body-bold text-[#101828]">{{ texts.graphTitle }}</div>
                <div class="body-small text-[#4A5565]">{{ texts.graphSubtitle }}</div>
            </div>
            <ImpactChart v-show="selectedCard === 'insights'" :data="insightsData?.history || null" :is-loading="isFetchingFilters || isFetchingInsights" />
            <ImpactChart v-show="selectedCard === 'relationships'" :data="relationshipsData?.history || null" :is-loading="isFetchingFilters || isFetchingRelationships" />
            <ImpactChart v-show="selectedCard === 'actionPlans'" :data="actionPlansData?.history || null" :is-loading="isFetchingFilters || isFetchingActionPlans" />
        </div>
        <InsightCard class="mt-8" :is-loading="isFetchingInsight" :content="insight?.content" />
    </div>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import { useCustomLabels } from "~vue/composables/useCustomLabels";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, ref, watch } from "vue";

import AnalyticsFilters from "../filters/AnalyticsFilters.vue";
import { getURLParamsFromFilters } from "../filters/helpers";
import ImpactCard from "../ImpactCard.vue";
import ImpactChart from "../ImpactChart.vue";
import InsightCard from "../InsightCard.vue";
import type { ImpactResponse, NadiaInsightResponse, SelectedFilters } from "../types";

const getLabel = useCustomLabels("client_analytics.impact_analysis");

const filters = ref<SelectedFilters>({});
const isFetchingFilters = ref(true);

const insightsUrl = ref("");
const relationshipsUrl = ref("");
const actionPlansUrl = ref("");

watch(
    filters,
    (newFilters) => {
        const params = getURLParamsFromFilters(newFilters);
        const queryString = params.toString();
        insightsUrl.value = `/api/client-analytics/impact/insights/?${queryString}`;
        relationshipsUrl.value = `/api/client-analytics/impact/relationships/?${queryString}`;
        actionPlansUrl.value = `/api/client-analytics/impact/action-plans/?${queryString}`;
    },
    { deep: true },
);

const selectedCard = ref<string>("insights");

const texts = computed(() => {
    switch (selectedCard.value) {
        case "insights":
            return {
                helpText:
                    "Every insight Nadia generates represents a moment of productive self-reflection. Research from Harvard Business School shows employees who regularly reflect on their work perform 23% better than those who don't.",
                graphTitle: `${getLabel("insights_created").value} Over Time`,
                graphSubtitle: "Monthly insights provided throughout the year",
            };
        case "relationships":
            return {
                helpText:
                    "Each People Card represents a workplace relationship Nadia is helping your employees actively manage. Gallup research shows employees who cultivate strong work relationships are 7x more likely to be engaged—and McKinsey identifies relationship quality as a top 5 driver of retention.",
                graphTitle: `${getLabel("relationships_managed").value} Over Time`,
                graphSubtitle: "Monthly relationships managed throughout the year",
            };
        case "actionPlans":
            return {
                helpText: "Specific next steps—research shows you're 2-3x more likely to follow through when you plan the when, where, and how (James Clear, Atomic Habits)",
                graphTitle: `${getLabel("plans_initiated").value} Over Time`,
                graphSubtitle: "Monthly action plans created throughout the year",
            };
        default:
            return {
                helpText: "",
                graphTitle: "",
                graphSubtitle: "",
            };
    }
});

const { data: insightsData, isFetching: isFetchingInsights, statusCode: insightsStatusCode } = useFetch(insightsUrl, { refetch: true }).json<ImpactResponse>();
const { data: relationshipsData, isFetching: isFetchingRelationships, statusCode: relationshipsStatusCode } = useFetch(relationshipsUrl, { refetch: true }).json<ImpactResponse>();
const { data: actionPlansData, isFetching: isFetchingActionPlans, statusCode: actionPlansStatusCode } = useFetch(actionPlansUrl, { refetch: true }).json<ImpactResponse>();
const { data: insight, isFetching: isFetchingInsight } = useFetch("/api/client-analytics/nadia-insights/impact_analysis", { immediate: true }).json<NadiaInsightResponse>();

const showKAnonError = computed(
    () =>
        (selectedCard.value === "insights" && insightsStatusCode.value === 451) ||
        (selectedCard.value === "relationships" && relationshipsStatusCode.value === 451) ||
        (selectedCard.value === "actionPlans" && actionPlansStatusCode.value === 451),
);

watch(
    selectedCard,
    (newCard) => {
        if (newCard) {
            logUserInteraction(AmplitudeEventNames.CLIENT_ANALYTICS_IMPACT_SUBTAB_SHOWN, { sub_tab: newCard });
        }
    },
    { immediate: true },
);
</script>
